import TableToolbar from '@rio-cloud/rio-uikit/lib/es/TableToolbar';
import React from 'react';
import { CustomFormattedMessage, useCustomIntl } from '../../../../i18n/i18n';
import Button from '@rio-cloud/rio-uikit/lib/es/Button';
import {
  getFromFilter,
  getPeriodFilter,
  getToFilter,
  getTransactionCurrenciesFilter,
  resetTransactionsFilters,
  setFrom,
  setPage,
  setPeriod,
  setServiceTypes,
  setTo,
  setTransactionsCurrencies,
  useDashboardFilteredTransactions,
  useTableFilteredTransactions,
} from '../../../../state/transactions/transactionFilterSlice';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import moment from 'moment/moment';
import Select from '@rio-cloud/rio-uikit/lib/es/Select';
import Multiselect from '@rio-cloud/rio-uikit/lib/es/Multiselect';
import { useCurrencyOptions } from '../../../../state/transactions/useTransactions';
import { CsvExportButton } from './CsvExportButton';
import { useTransactionUpdateState } from '../../../../state/transactions/transactionUpdateSlice';
import {
  usePeriodSelectOptions,
  parsePeriod,
} from '../../../../usePeriodSelectOptions';
import { ServiceType } from '../../../../codegen/transactions';
import { DateFilterPicker } from '../../../../components/DateFilterPicker';

export const TransactionsTableToolbar: React.FC = () => {
  const dispatch = useAppDispatch();
  const intl = useCustomIntl();
  const { transactionFilterPeriods } = usePeriodSelectOptions();

  const currencies = useAppSelector(getTransactionCurrenciesFilter);
  const from = moment(useAppSelector(getFromFilter));
  const to = moment(useAppSelector(getToFilter));
  const period = useAppSelector(getPeriodFilter);
  const { serviceTypeOptions } = useDashboardFilteredTransactions();

  const currencyOptions = useCurrencyOptions().map((c) => {
    return {
      ...c,
      selected: currencies.includes(c.id),
    };
  });

  const {
    hasTransactionUpdate,
    reset: resetTransactionUpdateState,
    setLastRefreshTime,
  } = useTransactionUpdateState();

  const { resetApiState } = useTableFilteredTransactions();

  const handleRefreshTransactions = () => {
    dispatch(setPage(1));
    resetApiState();
    setLastRefreshTime(Date.now());
    resetTransactionUpdateState();
  };

  return (
    <TableToolbar>
      <div
        className="table-toolbar-container"
        data-testid={'transactions-table-toolbar'}
      >
        <div className="table-toolbar-group-left gap-15 margin-bottom-15">
          <div className="btn-toolbar table-btn-toolbar">
            <DateFilterPicker
              labelId={'common.startDate'}
              value={from}
              onChange={(d) => dispatch(setFrom(d))}
              isDateBlocked={(d) => d.isAfter(to, 'day')}
              errorMessageId={(d) =>
                d.isAfter(to, 'day') && 'common.timerange.beforeEndDate'
              }
              data-testid={'transactions-table-start-date-picker'}
            />
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <DateFilterPicker
              labelId={'common.endDate'}
              value={to}
              onChange={(d) => dispatch(setTo(d))}
              isDateBlocked={(d) => d.isBefore(from, 'day')}
              errorMessageId={(d) =>
                (d.isBefore(from, 'day') &&
                  'common.timerange.afterStartDate') ||
                (d.isAfter(moment(), 'day') && 'common.timerange.beforeFuture')
              }
              data-testid={'transactions-table-end-date-picker'}
            />
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <div data-testid={'transactions-table-period-selector'}>
              <label>
                <CustomFormattedMessage id={'common.period'} />
              </label>
              <Select
                useClear
                className={'width-200'}
                id={'periodSelector'}
                placeholder={
                  <CustomFormattedMessage id={'common.selectPeriod'} />
                }
                options={transactionFilterPeriods}
                onChange={(option) => {
                  const value = option ? parsePeriod(option.id) : undefined;
                  dispatch(setPeriod(value));
                }}
                value={period ? [period.toString()] : []}
              />
            </div>
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <div data-testid="transactions-table-currency-filter">
              <label>
                <CustomFormattedMessage id={'common.currency'} />
              </label>
              <Multiselect
                className={'width-200'}
                placeholder={intl.formatMessage({
                  id: 'transactions.transactionsTable.toolbar.currencyFilterPlaceholder',
                })}
                id={'currencySelector'}
                options={currencyOptions}
                onChange={(selected: string[]) => {
                  dispatch(setTransactionsCurrencies(selected));
                }}
              />
            </div>
          </div>
          <div className="btn-toolbar table-btn-toolbar">
            <div data-testid="transactions-table-service-filter">
              <label>
                <CustomFormattedMessage
                  id={'dashboard.filter.serviceTypes.header'}
                />
              </label>
              <Multiselect
                className={'width-200'}
                placeholder={intl.formatMessage({
                  id: 'dashboard.filter.serviceTypes.placeholder',
                })}
                id={'serviceSelector'}
                options={serviceTypeOptions}
                onChange={(selected: string[]) =>
                  dispatch(setServiceTypes(selected as ServiceType[]))
                }
              />
            </div>
          </div>
        </div>
        <div className="table-toolbar-group-right">
          {hasTransactionUpdate && (
            <div className="table-toolbar-column">
              <div className="btn-toolbar table-btn-toolbar">
                <Button
                  data-testid="transactions-table-refresh-button"
                  bsStyle={'primary'}
                  onClick={handleRefreshTransactions}
                >
                  <span className={'rioglyph rioglyph-refresh'} />
                  <CustomFormattedMessage
                    id={'transactions.button.refreshTransactions'}
                  />
                </Button>
              </div>
            </div>
          )}
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <Button
                bsStyle={'primary'}
                onClick={() => {
                  dispatch(resetTransactionsFilters());
                }}
              >
                <span className={'rioglyph rioglyph-revert'} />
                <CustomFormattedMessage
                  id={'transactions.transactionsTable.toolbar.resetFilters'}
                />
              </Button>
            </div>
          </div>
          <div className="table-toolbar-column">
            <div className="btn-toolbar table-btn-toolbar">
              <CsvExportButton />
            </div>
          </div>
        </div>
      </div>
    </TableToolbar>
  );
};
